import React, { useEffect, useState } from 'react';
import { Box, IconButton, Tooltip } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { FiChevronLeft, FiChevronRight, FiEyeOff } from 'react-icons/fi';
import type { FloatingMenuPlacement } from '../../hooks/useDraggableFloat';

/**
 * LauncherChrome — the ONE place the floating-launcher numbers live.
 *
 * vault/how-to/draggable-floating-launchers.md § "One file owns the numbers". Six days after Tom
 * named `FloatingCalendarMenu` the size reference (56px FAB, 340px menu, ≥44px rows, a hide tab a
 * thumb can find), every shell launcher was still below it — not because anyone re-derived the
 * numbers wrong, but because they were hand-copied per launcher and drifted. A number that lives
 * in eight files is eight chances to lag. So the numbers live here and launchers compose them;
 * `components/__tests__/ModuleNavLauncher.test.tsx` pins them so a drift fails the build.
 *
 * Byte-identical in paradiselakescarecenter_fe and business-builder-master-frontend. Module
 * launchers (calendars, emails) that adopt it declare it under `### Shared files`.
 */
export const LAUNCHER = {
  /** The launcher button. Not 40, not 48, not 52 — 56 reads as a tap target, not a dot. */
  FAB_SIZE: 56,
  /** `preferredWidth` for computeMenuPlacement. Narrower feels cramped once real labels are in. */
  MENU_W: 340,
  /** Every tappable row / button inside a launcher menu. */
  ROW_MIN_H: 44,
  /** Padding from the viewport edges — hook `edgePad` and computeMenuPlacement `edgePad`. */
  EDGE_PAD: 12,
  /** FAB → menu gap for computeMenuPlacement. */
  MENU_GAP: 12,
  /** Design cap on a menu's height; ALWAYS min()'d with `placement.maxHeight` (see menuMaxH). */
  MENU_CAP: 640,

  /**
   * Resting drop shadow for a FAB, an open menu and an edge tab. A floating control with no
   * shadow sits IN the page instead of above it — on a white roster grid a hidden edge tab
   * without one reads as a rendering artifact rather than something you can press
   * (Tom, 2026-08-27). Raw `boxShadow` strings were the second-worst drift in the audit that
   * produced this file: eight launchers used six different values, from `lg` to a bespoke
   * `0 20px 60px rgba(0,0,0,.8)`.
   */
  SHADOW: '0 6px 16px rgba(0, 0, 0, 0.28)',
  /** Hover/focus shadow — deeper and higher, so the button reads as lifting toward the cursor. */
  SHADOW_HOVER: '0 12px 28px rgba(0, 0, 0, 0.34)',
  /** Menus and panels sit further off the page than the button that opened them. */
  SHADOW_MENU: '0 16px 44px rgba(0, 0, 0, 0.32)',
  /** How far a FAB lifts on hover, in px. Paired with SHADOW_HOVER — shadow alone reads flat. */
  LIFT: 2,
  /** How long the edge tab announces itself after the user hides a launcher, in ms. */
  ANNOUNCE_MS: 3000,

  /**
   * The z-index tier every floating launcher renders in (2026-09-05).
   *
   * A launcher sits ABOVE Chakra's `overlay` token (1300) and BELOW `modal` (1400). Both halves
   * are load-bearing:
   *
   *   - **Above 1300** because a page's own immersive overlay is not allowed to bury the app's
   *     NAVIGATION. Knowledge Explorer's full-screen mode is a `position:fixed` overlay at
   *     `overlay`, and phones open it BY DEFAULT — with the launchers underneath it, there was
   *     no way to navigate anywhere without exiting full screen first (Tom, 2026-09-05). The
   *     opposite rule used to be written into `ModuleNavLauncher` on purpose; this reverses it.
   *   - **Below 1400** so a modal, a drawer or a toast still covers the launcher. A FAB floating
   *     over an open dialog is the failure this ceiling prevents.
   *
   * The numbers live here for the same reason the sizes do: `FloatingCalendarNav` (1315),
   * `FloatingEventFilter` (1310) and `FloatingChatWidget` (1350) each picked their own, and the
   * two APP-SHELL launchers were left behind at 997–999 — the drift that produced this block.
   */
  /**
   * Clearance a bottom-anchored FAB leaves below itself, in px (2026-09-05). NOT the edge pad:
   * the bottom of a phone screen is where a composer, a Send button or a sticky action bar
   * lives, and a launcher parked 12px up sits ON it. `FloatingChatWidget` had already worked
   * this out independently (`vh - FAB_SIZE - 90`); the nav FAB was still at `vh - 76` — 20px of
   * clearance — which put it over Knowledge Explorer's chat composer on a 390px screen the
   * moment launchers started showing in full screen.
   */
  FAB_BOTTOM_CLEARANCE: 90,

  /** Secondary mini-FABs that hang off a launcher (e.g. the emergency logout). Below its FAB. */
  Z_MINI: 1305,
  /** The launcher button itself, and the edge tab it leaves behind when hidden. */
  Z_FAB: 1310,
  /** The open menu / panel — above the button that opened it. */
  Z_MENU: 1315,
} as const;

/**
 * The `maxH` every launcher menu renders with, beside `overflowY="auto"` and
 * `overscrollBehavior="contain"`. A fixed / `dvh` cap alone does NOT keep a menu on screen when
 * the FAB sits mid-viewport (the 2026-08-20 emails-nav finding) — `placement.maxHeight` is the
 * room on the side computeMenuPlacement chose, and this is how it gets applied.
 */
export const menuMaxH = (placement: Pick<FloatingMenuPlacement, 'maxHeight'>): string =>
  `min(${LAUNCHER.MENU_CAP}px, ${placement.maxHeight}px)`;

/** Chakra's `md` breakpoint, in px — the same cut every launcher uses for "is this a phone". */
export const MOBILE_MAX_WIDTH = 768;

/**
 * "Is this a phone" for launcher chrome. Deliberately `innerWidth` + a resize listener rather than
 * Chakra's `useBreakpointValue`: that hook needs `window.matchMedia`, which jsdom lacks, so every
 * test that renders the app shell (FloatingNavbar sits in most page tests) would have to shim it
 * — a rule nobody remembers. This works everywhere the shell renders, tests included.
 */
export const useLauncherIsMobile = (): boolean => {
  const read = () => typeof window !== 'undefined' && window.innerWidth < MOBILE_MAX_WIDTH;
  const [isMobile, setIsMobile] = useState<boolean>(read);
  useEffect(() => {
    const onResize = () => setIsMobile(read());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

/**
 * Does this viewer want motion suppressed?
 *
 * Nothing in this frontend honoured `prefers-reduced-motion` before 2026-08-27, and floating
 * chrome is exactly where it bites: a launcher that lifts, pulses or announces itself is motion
 * the user did not ask for, on every page, all shift. When the OS setting is on, every helper
 * below degrades to a plain state change — the shadow and the opacity bump still happen, because
 * those are contrast, not motion.
 *
 * `matchMedia` is feature-detected rather than assumed: jsdom lacks it, and every page test in
 * this repo renders the app shell.
 */
export const useLauncherReducedMotion = (): boolean => {
  const read = () => {
    try {
      return typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch { return false; }
  };
  const [reduced, setReduced] = useState<boolean>(read);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    // Safari < 14 has addListener only.
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
    else if (typeof mq.addListener === 'function') mq.addListener(onChange);
    return () => {
      if (typeof mq.removeEventListener === 'function') mq.removeEventListener('change', onChange);
      else if (typeof mq.removeListener === 'function') mq.removeListener(onChange);
    };
  }, []);
  return reduced;
};

/**
 * The shadow + hover-lift every launcher FAB renders with. Spread onto the button.
 *
 * Tom's call, 2026-08-27: launchers stay STILL at rest. No idle float, no periodic wiggle — five
 * FABs breathing on a roster grid all shift is noise staff stop seeing, and it competes with the
 * alerts that actually matter. The motion budget is spent where it does work: on hover/press
 * (this helper) and on the moment a launcher is hidden (`LauncherEdgeTab`'s `announce`).
 *
 *   <IconButton {...dragHandlers} {...launcherFabMotion(reducedMotion)} … />
 *
 * Anything the caller sets afterwards wins, so a launcher that genuinely needs its own resting
 * shadow (the Tom AI glow) can still override `boxShadow` on the next line.
 */
export const launcherFabMotion = (reducedMotion = false) => ({
  boxShadow: LAUNCHER.SHADOW,
  transition: reducedMotion ? 'box-shadow 0.2s ease' : 'transform 0.18s ease, box-shadow 0.18s ease',
  _hover: {
    boxShadow: LAUNCHER.SHADOW_HOVER,
    ...(reducedMotion ? {} : { transform: `translateY(-${LAUNCHER.LIFT}px)` }),
  },
  _focusVisible: {
    boxShadow: LAUNCHER.SHADOW_HOVER,
    ...(reducedMotion ? {} : { transform: `translateY(-${LAUNCHER.LIFT}px)` }),
  },
  _active: {
    boxShadow: LAUNCHER.SHADOW,
    ...(reducedMotion ? {} : { transform: 'translateY(1px)' }),
  },
});

/**
 * Click-outside for a launcher whose FAB and menu live in separate Portals. Listens on
 * `pointerdown`, not `mousedown`: a finger tap is a pointer event first, and on iOS the synthetic
 * `mousedown` can arrive after the tap has already landed on whatever sits under the menu.
 * `exemptSelectors` are the classNames of every container that must NOT close the menu — the FAB
 * wrapper, the menu wrapper, and any sibling mini-FAB.
 */
export function useLauncherClickOutside(
  open: boolean,
  exemptSelectors: string[],
  onOutside: () => void,
): void {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: Event) => {
      const el = e.target as HTMLElement | null;
      if (!el || typeof el.closest !== 'function') return;
      if (exemptSelectors.some((sel) => el.closest(sel))) return;
      onOutside();
    };
    // Both, deliberately: pointerdown is the real-world path; mousedown keeps jsdom (which has
    // no PointerEvent) and any pointer-events-off browser working. A pointer tap fires both, and
    // the second call is a no-op because `open` has already flipped by the time it runs.
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('mousedown', onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onOutside, exemptSelectors.join('|')]);
}

/**
 * The announce animation — a slide out of the edge, then three slow pulses.
 *
 * This is the ONLY motion a launcher makes on its own, and it exists to answer one question:
 * "where did it go?" You tap eye-off, the button disappears, and without this the tab is a
 * 14px sliver you never saw arrive (Tom, 2026-08-27). Three seconds of it, at the moment of
 * hiding, teaches the location once; after that the tab sits still.
 *
 * Separate keyframes per edge because the nudge points AWAY from the screen edge either way.
 */
const announceLeft = keyframes`
  0%   { opacity: 0; transform: translateX(-100%); }
  18%  { opacity: 1; transform: translateX(0); }
  40%  { opacity: 1; transform: translateX(7px); }
  60%  { opacity: 0.7; transform: translateX(0); }
  80%  { opacity: 1; transform: translateX(7px); }
  100% { opacity: 1; transform: translateX(0); }
`;
const announceRight = keyframes`
  0%   { opacity: 0; transform: translateX(100%); }
  18%  { opacity: 1; transform: translateX(0); }
  40%  { opacity: 1; transform: translateX(-7px); }
  60%  { opacity: 0.7; transform: translateX(0); }
  80%  { opacity: 1; transform: translateX(-7px); }
  100% { opacity: 1; transform: translateX(0); }
`;

interface EdgeTabProps {
  /** Which screen edge the tab hugs — match the launcher's home corner. */
  edge: 'left' | 'right';
  /** The launcher's last vertical position; clamped on screen here. */
  top: number;
  bg: string;
  label: string;
  zIndex: number;
  onClick: () => void;
  className?: string;
  /**
   * True for the first `LAUNCHER.ANNOUNCE_MS` after the user hid the launcher — pass
   * `useHideToEdge`'s `justHidden`. Runs the slide-and-pulse above so the user watches the
   * launcher land instead of wondering where it went. Ignored under reduced motion, where the
   * tab still renders at full opacity for the same window (contrast, not motion).
   */
  announce?: boolean;
}

/**
 * LauncherEdgeTab — ALL that renders while a launcher is hidden. It is the guarantee that hiding
 * can never strand the user, so it is never gated behind anything.
 *
 * 14×44 at 45% opacity was a third of the 44px minimum touch target, and this tab is the ONLY way
 * back once the menu is hidden — missing it feels like the menu is gone (Tom, 2026-08-17). Phones
 * get a target a thumb finds without looking; desktop keeps the slim tab a mouse hits exactly.
 */
export const LauncherEdgeTab: React.FC<EdgeTabProps> = ({ edge, top, bg, label, zIndex, onClick, className, announce = false }) => {
  const isMobile = useLauncherIsMobile();
  const reducedMotion = useLauncherReducedMotion();
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const h = isMobile ? 64 : 44;
  const clampedTop = Math.min(Math.max(LAUNCHER.EDGE_PAD, top), Math.max(LAUNCHER.EDGE_PAD, vh - h - LAUNCHER.EDGE_PAD));
  const Chevron = edge === 'left' ? FiChevronRight : FiChevronLeft;
  // 0.45 at rest was chosen when the tab had no shadow to separate it from the page. With one it
  // can sit lower-contrast and still read as a control, so the bump is modest — enough that a tab
  // on a white roster grid isn't mistaken for a rendering artifact (Tom, 2026-08-27).
  const restOpacity = isMobile ? 0.85 : 0.65;
  const seconds = LAUNCHER.ANNOUNCE_MS / 1000;
  return (
    <Tooltip hasArrow placement={edge === 'left' ? 'right' : 'left'} openDelay={300} label={label}>
      <Box
        as="button"
        className={className}
        aria-label={label}
        data-launcher-edge-tab={edge}
        data-announcing={announce ? 'true' : undefined}
        position="fixed"
        {...(edge === 'left' ? { left: 0 } : { right: 0 })}
        top={`${clampedTop}px`}
        w={isMobile ? '32px' : '14px'}
        h={`${h}px`}
        bg={bg}
        opacity={announce ? 1 : restOpacity}
        boxShadow={LAUNCHER.SHADOW}
        {...(edge === 'left' ? { borderRightRadius: 'md' } : { borderLeftRadius: 'md' })}
        zIndex={zIndex}
        display="flex"
        alignItems="center"
        justifyContent="center"
        color="white"
        _hover={{ opacity: 1, width: isMobile ? '36px' : '18px', boxShadow: LAUNCHER.SHADOW_HOVER }}
        transition="opacity 0.2s, width 0.2s, box-shadow 0.2s"
        animation={announce && !reducedMotion ? `${edge === 'left' ? announceLeft : announceRight} ${seconds}s ease-in-out 1` : undefined}
        onClick={onClick}
      >
        <Chevron size={isMobile ? 22 : 12} />
      </Box>
    </Tooltip>
  );
};

interface HideButtonProps {
  fabSize?: number;
  label: string;
  onClick: () => void;
  /** Pass the hook's `wasJustDragged` so a drag that ends on this button is not also a hide. */
  wasJustDragged?: () => boolean;
  zIndex?: number;
}

/**
 * LauncherHideButton — the eye-off mini-button hugging a FAB.
 *
 * A ~24px target on the corner of a 56px one is hit by accident when aiming for the FAB and
 * almost never when you mean it, so phones get a real `md` size. It sits top-RIGHT on phones,
 * not top-left: a FAB parked at `left: 16` put a left-offset button flush against x=0 and
 * impossible to hit cleanly. The right corner always has the FAB's own width behind it.
 */
export const LauncherHideButton: React.FC<HideButtonProps> = ({
  fabSize = LAUNCHER.FAB_SIZE, label, onClick, wasJustDragged, zIndex = 1,
}) => {
  const isMobile = useLauncherIsMobile();
  return (
    <Tooltip hasArrow placement="top" openDelay={300} label={label}>
      <IconButton
        aria-label={label}
        data-launcher-hide-button
        icon={<FiEyeOff size={isMobile ? 18 : undefined} />}
        size={isMobile ? 'md' : 'xs'}
        isRound
        position="absolute"
        top={isMobile ? '-12px' : '-8px'}
        left={isMobile ? `${fabSize - 24}px` : '-10px'}
        zIndex={zIndex}
        bg="blackAlpha.600"
        color="white"
        opacity={0.8}
        boxShadow={LAUNCHER.SHADOW}
        _hover={{ opacity: 1, bg: 'blackAlpha.800', boxShadow: LAUNCHER.SHADOW_HOVER }}
        onClick={(e) => {
          e.stopPropagation();
          if (wasJustDragged?.()) return;
          onClick();
        }}
      />
    </Tooltip>
  );
};
